import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { userAPI, suggestionsAPI } from "../../services/api";
import { invalidateHandlers } from "../../hooks/useHandlers";
import {
  CreateUserData,
  UpdateProfileData,
  UpdateUserData,
  UserState,
} from "../../types";

const initialState: UserState = {
  users: [],
  currentUser: null,
  suggestions: [],
  loading: false,
  error: null,
  totalFound: 0,
  searchQuery: "",
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    pages: 0,
  },
};

// Get Vehicle Suggestions
export const getUsersSuggestions = createAsyncThunk(
  "user/getUsersSuggestions",
  async (params: any, { rejectWithValue }) => {
    try {
      const response = await suggestionsAPI.getUsers(params);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch user suggestions"
      );
    }
  }
);

export const getUsers = createAsyncThunk(
  "user/getUsers",
  async (params: any = {}, { rejectWithValue }) => {
    try {
      const response = await userAPI.getUsers(params);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch users"
      );
    }
  }
);

export const getUser = createAsyncThunk(
  "user/getUser",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await userAPI.getUser(id);
      return response.data.data.user;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch user"
      );
    }
  }
);

export const createUser = createAsyncThunk(
  "user/createUser",
  async (userData: CreateUserData, { rejectWithValue }) => {
    try {
      const response = await userAPI.createUser(userData);
      // The handler picker's roster is cached for the session — a colleague
      // invited now has to be offerable without a reload.
      invalidateHandlers();
      return response.data.data.user;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to create user"
      );
    }
  }
);

export const updateUser = createAsyncThunk(
  "user/updateUser",
  async (
    { id, userData }: { id: string; userData: UpdateUserData },
    { rejectWithValue }
  ) => {
    try {
      const response = await userAPI.updateUser(id, userData);
      // A name or a role may have changed — both decide what the roster shows
      // and who is on it at all.
      invalidateHandlers();
      return response.data.data.user;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update user"
      );
    }
  }
);
export const updateUserProfile = createAsyncThunk(
  "user/updateUserProfile",
  async (
    { userData }: { userData: UpdateProfileData },
    { rejectWithValue }
  ) => {
    try {
      const response = await userAPI.updateProfile(userData);
      localStorage.setItem("user", JSON.stringify(response.data.data.user));
      return response.data.data.user;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update user"
      );
    }
  }
);

export const deleteUser = createAsyncThunk(
  "user/deleteUser",
  async (id: string, { rejectWithValue }) => {
    try {
      await userAPI.deleteUser(id);
      // Somebody who has left must stop being offered. Records they already
      // handle keep them — that is a historical fact, and the bubble resolves
      // it from the record's own populated ref rather than from this roster.
      invalidateHandlers();
      return id;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete user"
      );
    }
  }
);

export const deleteMultipleUsers = createAsyncThunk(
  "users/deleteMultipleUsers",
  async (ids: string[], { rejectWithValue }) => {
    try {
      await userAPI.deleteMultipleUsers(ids);
      invalidateHandlers();
      return ids;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete users"
      );
    }
  }
);

export const toggleUserStatus = createAsyncThunk(
  "user/toggleUserStatus",
  async (
    { id, status }: { id: string; status: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await userAPI.toggleUserStatus(id, status);
      // The roster is active users only: deactivating somebody has to take
      // them out of the picker, and reactivating has to put them back.
      invalidateHandlers();
      return response.data.data.user;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to toggle user status"
      );
    }
  }
);

export const resendInvite = createAsyncThunk(
  "user/resendInvite",
  async (id: string, { rejectWithValue }) => {
    try {
      await userAPI.resendInvite(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to resend invite"
      );
    }
  }
);

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentUser: (state) => {
      state.currentUser = null;
    },
    clearSuggestions: (state) => {
      state.suggestions = [];
      state.totalFound = 0;
      state.searchQuery = "";
    },
  },
  extraReducers: (builder) => {
    builder
      // Get Vehicle Suggestions
      .addCase(getUsersSuggestions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getUsersSuggestions.fulfilled, (state, action) => {
        state.loading = false;
        state.suggestions = action.payload.suggestions;
        state.totalFound = action.payload.totalFound;
        state.searchQuery = action.payload.searchQuery;
        state.error = null;
      })
      .addCase(getUsersSuggestions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.suggestions = [];
        state.totalFound = 0;
      })
      // Get Users
      .addCase(getUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.users = action.payload.users;
        state.pagination = action.payload.pagination;
        state.error = null;
      })
      .addCase(getUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Get User
      .addCase(getUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getUser.fulfilled, (state, action) => {
        state.loading = false;
        state.currentUser = action.payload;
        state.error = null;
      })
      .addCase(getUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Create User
      .addCase(createUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createUser.fulfilled, (state, action) => {
        state.loading = false;
        state.users.unshift(action.payload);
        state.error = null;
      })
      .addCase(createUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Update User
      .addCase(updateUser.fulfilled, (state, action) => {
        const index = state.users.findIndex(
          (user) => user._id === action.payload._id
        );
        if (index !== -1) {
          state.users[index] = action.payload;
        }
        if (state.currentUser?._id === action.payload._id) {
          state.currentUser = action.payload;
        }
      })
      // Update User Profile
      .addCase(updateUserProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateUserProfile.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(updateUserProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Delete User
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.users = state.users.filter((user) => user._id !== action.payload);
        state.pagination.total -= 1;
        state.loading = false;
        state.error = null;
      })
      .addCase(deleteUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Delete Multiple Users
      .addCase(deleteMultipleUsers.fulfilled, (state, action) => {
        state.users = state.users.filter(
          (user) => !action.payload.includes(user._id)
        );
        state.pagination.total -= action.payload.length;
        state.loading = false;
        state.error = null;
      })
      .addCase(deleteMultipleUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteMultipleUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Toggle User Status
      .addCase(toggleUserStatus.fulfilled, (state, action) => {
        const index = state.users.findIndex(
          (user) => user._id === action.payload._id
        );
        if (index !== -1) {
          state.users[index] = action.payload;
        }
      });
  },
});

export const { clearError, clearCurrentUser, clearSuggestions } =
  userSlice.actions;
export default userSlice.reducer;
